import type { TravelerHomeCountry } from 'src/types/market.type';
import { queryFilter } from 'utils/proptypes';
import base from './baseApi';
import { createSearchParams } from 'react-router';
import { omitBy } from 'lodash';
import { getHostedFormAccentColor } from 'utils/color.util';

const { urls, methods, execute, get, post, del, patch } = base();

const getUsers = async (queryFilter: queryFilter & { isBooking?: boolean; siteId?: string }) => {
  const method = methods.GET;
  const initParams: any = omitBy(queryFilter, v => v === '');
  const params = createSearchParams(initParams);

  const url = `${urls.user.getUsers}?${params.toString()}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getSiteUsers = async (queryFilter: queryFilter) => {
  const method = methods.GET;
  const initParams: any = omitBy(queryFilter, v => v === '');
  const params = createSearchParams(initParams);

  const url = `${urls.user.siteUsers}?${params.toString()}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getCurrentUser = () => {
  const method = methods.GET;
  const url = `${urls.user.getCurrentUser}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

const updateUserProfile = (payload: any) => {
  const method = methods.PUT;
  const url = `${urls.user.userProfile}`;
  const response = execute(method, url, { authorization: true }, payload);

  return response;
};

const updateSiteUser = ({ id, payload }: any) => {
  const method = methods.PUT;
  const url = `${urls.user.siteUsers}/${id}`;
  const response = execute(method, url, { authorization: true }, payload);

  return response;
};

const createSiteUser = ({ payload }: any) => {
  const method = methods.POST;
  const url = urls.user.siteUsers;

  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const changeEmailAddress = (id: string, emailAddress: string) => {
  const method = methods.GET;
  const url = `${urls.user.setRoles}/?id=${id}&emailAddress=${emailAddress}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

const updateActiveUserById = (payload: { id: string, isActive: boolean }) => {
  const method = methods.PATCH;
  const url = `${urls.user.users}/${payload.id}`;
  const response = execute(method, url, { authorization: true }, { isActive: payload.isActive });

  return response;
};

const setRolesForUser = (payload: { userId: string, roleIds: string[] }) => {
  const method = methods.GET;
  const url = urls.user.setRoles;
  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const updateUserImage = (payload: { id: string, file?: any }) => {
  const { id, file } = payload;
  const method = methods.POST;
  const url = `${urls.user.users}/${id}/uploadimage`;
  const response = execute(method, url, { authorization: true }, { file }, true);
  return response;
};

const confirmAndSaveProfileImage = (payload: { userId: string, fileName?: string }) => {
  const method = methods.PUT;
  const url = `${urls.user.users}/confirmAndSaveFile`;
  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const getUsersByEmail = async (emailAddress: string) => {
  const method = methods.GET;
  const url = `${urls.user.userEmail}?emailAddress=${emailAddress}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getUserById = async (id: string) => {
  const method = methods.GET;
  const url = `${urls.user.users}/${id}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const assignRoles = ({ id, payload }: any) => {
  const method = methods.PUT;
  const url = `${urls.user.users}/${id}/roles`;
  return execute(method, url, { authorization: true }, payload);
};

/**
 * Saved cards. With `onBehalfOfUserId` (Platform Admin only, NPT-2092) these are the traveler's
 * cards, for booking on their behalf at checkout.
 */
const getUserCards = (onBehalfOfUserId?: string) => {
  const method = methods.GET;
  const url = onBehalfOfUserId ? `${urls.user.cards}?${createSearchParams({ onBehalfOfUserId })}` : `${urls.user.cards}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

/** NPT-2083 — what the Help Scout Beacon's identify call needs. `signature` is null when the API has no Secure Mode key. */
export type SupportIdentity = { name: string; email: string; signature: string | null };

const getSupportIdentity = () => execute(methods.GET, urls.user.supportIdentity, { authorization: true });

/**
 * Body both hosted-page token calls send. `accentColor` is the live theme primary — the API
 * forwards it as Authorize.net's hostedProfileHeadingBgColor so the hosted form's field lines
 * and Save button pick up the site/org brand instead of stock grey. Omitted when the theme var
 * isn't a plain hex; the API then leaves the setting off entirely.
 */
const hostedPageTokenBody = () => {
  const accentColor = getHostedFormAccentColor();
  return accentColor ? { siteOrigin: window.location.origin, accentColor } : { siteOrigin: window.location.origin };
};

/** NPT-2092 — adds `onBehalfOfUserId` to a card request body only when booking for a traveler. */
const withOnBehalf = <T extends object>(body: T, onBehalfOfUserId?: string) => (onBehalfOfUserId ? { ...body, onBehalfOfUserId } : body);

/**
 * Accept Customer hosted profile page (SAQ A): fetch a single-use page token — card entry
 * itself happens on Authorize.net's page inside an iframe.
 */
const getHostedProfilePageToken = (recaptchaToken?: string | null, isCheckbox = false, onBehalfOfUserId?: string) => {
  const url = `${urls.user.hostedProfilePageToken}`;
  const headers: Record<string, any> = {};
  if (recaptchaToken) {
    headers['x-recaptcha-token'] = recaptchaToken;
    if (isCheckbox) headers['x-recaptcha-checkbox'] = 'true';
  }
  return post(url, withOnBehalf(hostedPageTokenBody(), onBehalfOfUserId), headers);
};

/**
 * Hosted editPayment page token for an existing saved card (fix a wrong zip/expiry/PAN).
 * The response's paymentProfileId must be POSTed to the editPayment URL alongside the token.
 */
const getHostedCardEditPageToken = (cardId: string, recaptchaToken?: string | null, isCheckbox = false) => {
  const url = `${urls.user.hostedCardEditPageToken.replace(':cardId', cardId)}`;
  const headers: Record<string, any> = {};
  if (recaptchaToken) {
    headers['x-recaptcha-token'] = recaptchaToken;
    if (isCheckbox) headers['x-recaptcha-checkbox'] = 'true';
  }
  return post(url, hostedPageTokenBody(), headers);
};

/** After the hosted profile page saves a card, pull the new profile into user_card rows. */
const syncUserCards = (onBehalfOfUserId?: string) => {
  const url = `${urls.user.syncCards}`;
  return post(url, withOnBehalf({}, onBehalfOfUserId), {});
};

export type TravelerAccount = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  /** False when the account already existed (the email was matched). */
  created: boolean;
  /** Whether the traveler has signed in before; if not, their confirmation email has a claim link. */
  hasAccount: boolean;
};

/** NPT-2091 — Platform Admin: create the traveler (or reuse the account with this email). */
const createTraveler = (payload: { firstName: string; lastName: string; emailAddress: string }) =>
  execute(methods.POST, urls.user.travelers, { authorization: true }, payload);

/** NPT-2091 — public: the email behind a claim link, to prefill sign-up. 404 when the link is invalid. */
const resolveTravelerClaim = (token: string) =>
  execute(methods.GET, urls.user.travelerClaim.replace(':token', encodeURIComponent(token)), { authorization: false });

const setDefaultUserCard = (cardId: any) => {
  const url = `${urls.user.setDefaultCard.replace(':cardId', cardId)}`;
  return get(url);
};

const deleteUserCard = (cardId: any) => {
  const url = `${urls.user.deleteCard.replace(':cardId', cardId)}`;
  return del(url);
};

/** Correct the cardholder name on a saved card — the hosted pages have no name field. Send null to clear it back to unknown. */
const updateUserCardName = (cardId: string, cardHolderName: string | null) => {
  const url = `${urls.user.updateCard.replace(':cardId', cardId)}`;
  return patch(url, { cardHolderName });
};

/**
 * NPT-1957 — the name and billing block a traveller edits on a saved card, in one PATCH to the same
 * route as the name alone. The route only touches keys that are sent: leave one out to keep it,
 * send null or '' to clear it. billingAddress and billingZip only ever travel together.
 */
export type CardDetailsPayload = {
  cardHolderName?: string | null;
  billingAddress?: string;
  billingZip?: string;
  billingCity?: string | null;
  billingState?: string | null;
  billingFirstName?: string | null;
  billingLastName?: string | null;
  billingCompany?: string | null;
  /** NPT-2008 — ISO-2 country of the billing address (US, CA or MX). */
  billingCountry?: TravelerHomeCountry;
};

const updateUserCardDetails = (cardId: string, details: CardDetailsPayload) => {
  const url = `${urls.user.updateCard.replace(':cardId', cardId)}`;
  return patch(url, details);
};

export {
  assignRoles,
  changeEmailAddress,
  confirmAndSaveProfileImage,
  createSiteUser,
  getCurrentUser,
  getSiteUsers,
  getUserById,
  getUsers,
  getUsersByEmail,
  setRolesForUser,
  updateActiveUserById,
  updateSiteUser,
  updateUserImage,
  updateUserProfile,
  getUserCards,
  getSupportIdentity,
  getHostedProfilePageToken,
  getHostedCardEditPageToken,
  syncUserCards,
  createTraveler,
  resolveTravelerClaim,
  setDefaultUserCard,
  deleteUserCard,
  updateUserCardName,
  updateUserCardDetails,
};
